import { useForm, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { usePage } from '@inertiajs/react'
import { useOrgPath } from '@/lib/pathFor'
import { capitalize } from '@/lib/utils'

interface UserDetail {
  id: string
  name: string | null
  email_address: string
  role: 'admin' | 'operator'
  status: 'active' | 'deactivated'
}

interface Props {
  user: UserDetail
}

export default function UsersEdit({ user }: Props) {
  const { props } = usePage<{ current_user: { id: string } }>()
  const isSelf = props.current_user?.id === user.id
  const pathFor = useOrgPath()

  const { data, setData, patch, processing } = useForm({
    name: user.name || '',
    email_address: user.email_address,
    password: '',
    role: user.role,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(pathFor(`/settings/users/${user.id}`))
  }

  function handleToggleStatus() {
    const action = user.status === 'active' ? 'deactivate' : 'reactivate'
    router.post(pathFor(`/settings/users/${user.id}/${action}`))
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <h1 className="text-sm font-semibold">Edit User</h1>
          <Badge
            variant="secondary"
            size="sm"
            className={`font-medium ${
              user.status === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {capitalize(user.status)}
          </Badge>
        </div>
        {!isSelf && (
          <Button
            variant="outline"
            size="sm"
            className={`h-8 text-xs ${user.status === 'active' ? 'text-red-600 hover:text-red-700' : ''}`}
            onClick={handleToggleStatus}
          >
            {user.status === 'active' ? 'Deactivate' : 'Reactivate'}
          </Button>
        )}
      </header>

      <div className="max-w-md p-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-xs">Name</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div>
            <Label htmlFor="email_address" className="text-xs">Email</Label>
            <Input
              id="email_address"
              type="email"
              required
              value={data.email_address}
              onChange={(e) => setData('email_address', e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-xs">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Leave blank to keep current"
              value={data.password}
              onChange={(e) => setData('password', e.target.value)}
              className="mt-1 h-9 text-[13px]"
            />
          </div>

          <div>
            <Label htmlFor="role" className="text-xs">Role</Label>
            <select
              id="role"
              value={data.role}
              onChange={(e) => setData('role', e.target.value as 'operator' | 'admin')}
              disabled={isSelf}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
            >
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
            {isSelf && (
              <p className="mt-1 text-xs text-muted-foreground">You cannot change your own role.</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing}>
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

UsersEdit.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
